/**
 * Fila de una marca en el historial (006 · T031; contracts/staff-ui.md §4, wiring 006 §0.2/§0.4).
 *
 * Fila de 48 px, sin ninguna acción (FR-008): solo lectura, ni botón ni enlace. La hora se formatea
 * SIEMPRE con `site_timezone` de la marca (nunca la zona actual de la sede, wiring 006 §0.3). Tres
 * indicadores, todos texto + (solo "Observada") icono: "(ajustada)" (`source = MANUAL`), "Observada"
 * (cualquier `FLAGGED`, sin exponer el flag ni el motivo) y, solo con `MISSING_CHECKOUT`, además
 * "Sin salida registrada". El servidor ya tradujo `adjusted`/`observation`: aquí no se interpreta
 * ningún dato técnico (`status`, `flags`, coordenadas…), que nunca llega a Staff.
 *
 * 007 · T036/T057 (contracts/ui.md §1.3, research R-23): bajo la marca **ancla** de un hueco, el
 * enlace `history-mark-regularize` "Regularizar" (si el servidor dice `can_request` y la empresa no
 * está suspendida) o la etiqueta de estado `history-mark-regularization` (icono + texto). Tras un
 * rechazo o una cancelación con cupo vuelve a ser "Regularizar". Es la **única** acción de la
 * pestaña y no modifica la marca. Una entrada "Sin salida registrada" sin ítem de regularización
 * (fuera del plazo) dice "Tu administrador puede corregirla." (US1-9). La unión con el historial es
 * de presentación, por `anchor.mark_id`: el contrato del historial no cambia.
 */
import { Link } from '@tanstack/react-router';
import { Eye } from 'lucide-react';

import type { StaffAttendanceMark, StaffRegularizationItem } from '@fermin/contracts';

import { formatTime, t } from '../../i18n';
import { STATE_ICON, stateLabel } from '../regularization/state-badge';

export interface MarkRowProps {
  mark: StaffAttendanceMark;
  /**
   * Ítem de regularización cuyo ancla es esta marca: `null` si no hay ninguno, `undefined` si las
   * regularizaciones aún no se cargaron (o fallaron): entonces no se pinta nada de la 007.
   */
  regularization?: StaffRegularizationItem | null;
  /** Empresa suspendida: estados sin acciones (FR-024). */
  readOnly?: boolean;
  /** Sin red: el enlace "Regularizar" queda deshabilitado. */
  online?: boolean;
}

const REGULARIZE_LINK_CLASSES =
  'inline-flex min-h-11 w-fit items-center font-ui text-fm-base font-semibold text-fm-primary-text ' +
  'underline underline-offset-4 rounded-fm-sm focus-visible:outline-2 focus-visible:outline-offset-2 ' +
  'focus-visible:outline-fm-focus';

function RegularizationHint({
  mark,
  regularization,
  readOnly,
  online,
}: Required<Pick<MarkRowProps, 'mark' | 'readOnly' | 'online'>> & {
  regularization: StaffRegularizationItem | null;
}) {
  if (regularization === null) {
    return mark.observation === 'MISSING_CHECKOUT' ? (
      <span data-testid="history-mark-admin-fix" className="text-fm-sm text-fm-text-secondary">
        {t('regularization.history.adminCanFix')}
      </span>
    ) : null;
  }
  if (regularization.can_request && !readOnly) {
    return online ? (
      <Link
        to="/regularizar/$markId"
        params={{ markId: mark.id }}
        data-testid="history-mark-regularize"
        className={REGULARIZE_LINK_CLASSES}
      >
        {t('regularization.history.regularize')}
      </Link>
    ) : (
      <button
        type="button"
        data-testid="history-mark-regularize"
        disabled
        className={`${REGULARIZE_LINK_CLASSES} cursor-not-allowed opacity-45`}
      >
        {t('regularization.history.regularize')}
      </button>
    );
  }
  const Icon = STATE_ICON[regularization.state];
  return (
    <span
      data-testid="history-mark-regularization"
      data-state={regularization.state}
      className="flex items-center gap-fm-1 text-fm-sm text-fm-text-secondary"
    >
      <Icon aria-hidden="true" size={14} />
      {stateLabel(regularization.state)}
    </span>
  );
}

export function MarkRow({ mark, regularization, readOnly = false, online = true }: MarkRowProps) {
  const time = formatTime(mark.server_timestamp, mark.site_timezone);
  const typeLabel = t(`history.markType.${mark.type}`);

  return (
    <li
      data-testid="history-mark"
      data-type={mark.type}
      className="flex min-h-12 items-center justify-between gap-fm-3 py-fm-2"
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-fm-base font-ui font-semibold text-fm-text">
          {typeLabel}
          {mark.adjusted ? (
            <span
              data-testid="history-mark-adjusted"
              className="ml-fm-1 font-ui font-normal text-fm-text-secondary"
            >
              {t('history.indicators.adjusted')}
            </span>
          ) : null}
        </span>
        <span className="text-fm-sm text-fm-text-secondary">{mark.site.name}</span>
        {mark.observation !== null ? (
          <span
            data-testid="history-mark-observed"
            className="flex items-center gap-fm-1 text-fm-sm text-fm-text-secondary"
          >
            <Eye aria-hidden="true" size={14} />
            {t('history.indicators.observed')}
          </span>
        ) : null}
        {mark.observation === 'MISSING_CHECKOUT' ? (
          <span
            data-testid="history-mark-missing-checkout"
            className="text-fm-sm text-fm-text-secondary"
          >
            {t('history.indicators.missingCheckout')}
          </span>
        ) : null}
        {regularization !== undefined ? (
          <RegularizationHint
            mark={mark}
            regularization={regularization}
            readOnly={readOnly}
            online={online}
          />
        ) : null}
      </div>
      <span className="fm-tabular shrink-0 font-display text-fm-lg font-bold text-fm-text">
        {time}
      </span>
    </li>
  );
}
